/**
 * Collaborative WYSIWYG editor backed by a durable server-side Yjs document.
 * Shares Markdown elements and input rules with description editors.
 * Collaboration via Yjs through TernProvider.
 */
import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { useTranslation } from "react-i18next";
import { Bold, Italic, Strikethrough, Code, Link2, CircleDot, ListChecks, Link as LinkIcon, ListPlus } from "lucide-react";
import * as Y from "yjs";
import { TernProvider, type CollaborationState } from "./provider";
import { markdownExtensions } from "./markdownExtensions";
import { SlashMenu } from "./SlashMenu";
import { TableToolbar } from "./TableToolbar";
import { TernLinkStatus, parseTernHref, type LinkStatus, type TernTarget } from "./ternLinks";
import type { ProjectData } from "@/lib/store";
import { useProjectStore } from "@/lib/store";

export type Selection = { text: string; from: number; to: number; listItems: { text: string; from: number; to: number }[] };

export type TernEditorHandle = {
  /** Wrap [from,to] in a tern: link (after the issue/task was created). */
  linkRange: (from: number, to: number, href: string) => void;
  getMarkdown: () => string;
  scrollToTernLink: (href: string, occurrence: number) => void;
};

type TernEditorProps = {
  d: ProjectData;
  docId: string;
  editable: boolean;
  user: { id: string; name: string; color: string };
  onMarkdownChange: (md: string) => void;
  onCreateIssue: (sel: Selection) => void;
  onCreateTask: (sel: Selection) => void;
  onLinkExisting: (sel: Selection) => void;
  onBulkCreate: (sel: Selection) => void;
  onReady: (h: TernEditorHandle) => void;
  onStatus: (s: string) => void;
  onCollaboration: (s: CollaborationState) => void;
};

type EditorSession = { docId: string; ydoc: Y.Doc; provider: TernProvider };

export function TernEditor(props: TernEditorProps) {
  const { t } = useTranslation();
  const [session, setSession] = useState<EditorSession | null>(null);
  const docId = props.docId;

  useEffect(() => {
    const ydoc = new Y.Doc();
    const provider = new TernProvider(docId, ydoc);
    // The editor needs the external session created here. Keeping sockets out of
    // render also lets Strict Mode clean up and recreate a working connection.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSession({ docId, ydoc, provider });
    return () => {
      provider.destroy();
      ydoc.destroy();
    };
  }, [docId]);

  if (!session || session.docId !== docId) return <div className="min-h-[60vh] text-muted">{t("common.loading")}</div>;
  return <ConnectedEditor key={docId} {...props} ydoc={session.ydoc} provider={session.provider} />;
}

function ConnectedEditor({
  d, docId, editable, user, onMarkdownChange,
  onCreateIssue, onCreateTask, onLinkExisting, onBulkCreate, onReady, onStatus,
  ydoc, provider, onCollaboration,
}: TernEditorProps & Pick<EditorSession, "ydoc" | "provider">) {
  const { t } = useTranslation();
  const [synced, setSynced] = useState(provider.isSynced);
  const seeded = useRef(false);
  const [problem, setProblem] = useState(false);
  useEffect(() => provider.onState((state) => { setProblem(!!state.problem); onCollaboration(state); }), [provider, onCollaboration]);

  useEffect(() => {
    const off = provider.onStatus(onStatus);
    const offSynced = provider.onceSynced(() => setSynced(true));
    return () => {
      off();
      offSynced();
    };
  }, [provider, onStatus]);

  const getStatus = (target: TernTarget): LinkStatus => {
    // Look up live data from every loaded project (docs may reference other projects' issues).
    const all = Object.values(useProjectStore.getState().byId);
    if (target.kind === "issue") {
      const [key, num] = target.identifier.split("-");
      for (const p of all) {
        if (p.project.key !== key) continue;
        const issue = Object.values(p.issues).find((i) => i.number === Number(num) && !i.deleted_at);
        if (issue) return (p.states[issue.state_id]?.type as LinkStatus) ?? "unknown";
      }
    } else {
      for (const p of all) {
        const task = p.tasks[target.id];
        if (task) return task.status === "completed" ? "completed" : task.status === "canceled" ? "canceled" : task.status === "done" ? "done" : task.status === "pending" ? "pending" : task.status === "started" ? "started" : task.status === "planned" ? "unstarted" : "backlog";
      }
    }
    return "unknown";
  };

  const editor = useEditor(
    {
      editable: editable && synced && !problem,
      enablePasteRules: false,
      extensions: [
        ...markdownExtensions(t("editor.placeholder"), true, t("editor.placeholder")),
        Collaboration.configure({ document: ydoc }),
        CollaborationCaret.configure({ provider, user }),
        TernLinkStatus.configure({ getStatus }),
      ],
      editorProps: {
        attributes: { class: "prose-tern prose-doc tern-editor outline-none focus-visible:outline-none min-h-[60vh]", role: "textbox", "aria-label": t("docs.placeholder"), "aria-multiline": "true", "aria-busy": String(!synced) },
        handleClickOn: (_view, _pos, node, _np, _event, direct) => {
          if (!direct) return false;
          const mark = node.marks?.find((m) => m.type.name === "link");
          const target = parseTernHref(mark?.attrs.href as string | undefined);
          if (target) {
            window.dispatchEvent(new CustomEvent("tern:open-link", { detail: target }));
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor }) => {
        if (!seeded.current) return;
        onMarkdownChange(editor.getMarkdown());
      },
    },
    [docId],
  );

  useEffect(() => {
    editor?.setEditable(editable && synced && !problem, false);
  }, [editor, editable, synced, problem]);

  // The server supplies the canonical CRDT, including the first Markdown import.
  useEffect(() => {
    if (!editor || !synced || seeded.current) return;
    seeded.current = true;
    onReady({
      linkRange: (from, to, href) => {
        editor.chain().focus().setTextSelection({ from, to }).setLink({ href }).run();
        onMarkdownChange(editor.getMarkdown());
      },
      getMarkdown: () => editor.getMarkdown(),
      scrollToTernLink: (href, occurrence) => {
        let n = -1;
        editor.state.doc.descendants((node, pos) => {
          if (!node.isText) return;
          if (node.marks.some((m) => m.type.name === "link" && m.attrs.href === href)) {
            n += 1;
            if (n === occurrence) {
              editor.commands.setTextSelection({ from: pos, to: pos + node.nodeSize });
              const el = editor.view.domAtPos(pos).node as HTMLElement;
              (el.nodeType === 3 ? el.parentElement : el)?.scrollIntoView({ block: "center", behavior: "smooth" });
              (el.nodeType === 3 ? el.parentElement : el)?.classList.add("tern-flash");
              return false;
            }
          }
        });
      },
    });
  }, [editor, synced, onReady, onMarkdownChange]);

  // Re-decorate links when store data changes (status colors).
  const version = useProjectStore((s) => s.byId[d.project.id]?.version);
  useEffect(() => {
    if (editor) editor.view.dispatch(editor.state.tr.setMeta("refreshTernLinks", true));
  }, [editor, version]);

  const selection = (): Selection | null => {
    if (!editor) return null;
    const { from, to } = editor.state.selection;
    if (from === to) return null;
    const text = editor.state.doc.textBetween(from, to, "\n").trim();
    const listItems: Selection["listItems"] = [];
    editor.state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type.name === "listItem" || node.type.name === "taskItem") {
        const para = node.firstChild;
        if (para) {
          const s = pos + 1;
          const e = s + para.nodeSize - 1;
          const itemText = editor.state.doc.textBetween(s + 1, e - 1, " ").trim();
          if (itemText) listItems.push({ text: itemText, from: s + 1, to: e - 1 });
        }
        return false;
      }
      return true;
    });
    return text ? { text, from, to, listItems } : null;
  };

  const btn = "flex h-7 w-7 items-center justify-center rounded text-muted hover:bg-hover hover:text-fg data-[active=true]:text-fg data-[active=true]:bg-active";
  return (
    <div className="relative">
      {editor && editable && (
        <BubbleMenu editor={editor} className="flex items-center gap-0.5 rounded-lg border border-border bg-elevated p-1 shadow-xl" options={{ placement: "top" }}>
          <button className={btn} data-active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold className="h-3.5 w-3.5" /></button>
          <button className={btn} data-active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic className="h-3.5 w-3.5" /></button>
          <button className={btn} data-active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strike"><Strikethrough className="h-3.5 w-3.5" /></button>
          <button className={btn} data-active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()} title="Code"><Code className="h-3.5 w-3.5" /></button>
          <button className={btn} data-active={editor.isActive("link")} onClick={() => {
            const href = window.prompt(t("docs.linkPrompt"), (editor.getAttributes("link").href as string) ?? "https://");
            if (href === null) return;
            if (href === "") editor.chain().focus().unsetLink().run();
            else editor.chain().focus().setLink({ href }).run();
          }} title="Link"><LinkIcon className="h-3.5 w-3.5" /></button>
          <span className="mx-1 h-4 w-px bg-border" />
          <button data-tern-action="i" className="flex h-7 items-center gap-1 rounded px-2 text-[12px] text-fg hover:bg-hover" onClick={() => { const s = selection(); if (s) onCreateIssue(s); }} title="⌥I"><CircleDot className="h-3.5 w-3.5 text-accent" /> Issue</button>
          <button data-tern-action="t" className="flex h-7 items-center gap-1 rounded px-2 text-[12px] text-fg hover:bg-hover" onClick={() => { const s = selection(); if (s) onCreateTask(s); }} title="⌥T"><ListChecks className="h-3.5 w-3.5 text-accent" /> Task</button>
          <button data-tern-action="l" className="flex h-7 items-center gap-1 rounded px-2 text-[12px] text-muted hover:bg-hover hover:text-fg" onClick={() => { const s = selection(); if (s) onLinkExisting(s); }} title="⌥L"><Link2 className="h-3.5 w-3.5" /> {t("docs.linkExisting")}</button>
          {(selection()?.listItems.length ?? 0) > 1 && (
            <button className="flex h-7 items-center gap-1 rounded px-2 text-[12px] text-muted hover:bg-hover hover:text-fg" onClick={() => { const s = selection(); if (s) onBulkCreate(s); }}><ListPlus className="h-3.5 w-3.5" /> {t("docs.bulkCreate", { n: selection()?.listItems.length ?? 0 })}</button>
          )}
        </BubbleMenu>
      )}
      <TableToolbar editor={editor} />
      <EditorContent editor={editor} />
      <SlashMenu editor={editor} projectId={d.project.id} />
    </div>
  );
}
